import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IoArrowBack, 
  IoSendOutline, 
  IoCheckmarkCircleOutline,
  IoAlertCircleOutline
} from 'react-icons/io5';
import { useLanguage } from './contexts/LanguageContext';
import './FeedbackPage.css';

function FeedbackPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    type: 'feedback',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          type: 'feedback',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const isFormValid = formData.message.trim();

  return (
    <div className="feedback-container">
      <div className="feedback-header">
        <button className="back-button" onClick={handleBack}>
          <IoArrowBack size={20} />
          {t('back')}
        </button>
        <div className="header-content">
          <h1>{t('feedbackTitle')}</h1>
          <p>{t('feedbackSubtitle')}</p>
        </div>
      </div>

      <div className="feedback-content">
        <div className="feedback-form-container">
          {submitStatus === 'success' && (
            <div className="status-message success">
              <IoCheckmarkCircleOutline size={24} />
              <div>
                <h3>{t('feedbackSuccess')}</h3>
                <p>{t('feedbackSuccessMessage')}</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="status-message error">
              <IoAlertCircleOutline size={24} />
              <div>
                <h3>{t('feedbackError')}</h3>
                <p>{t('feedbackErrorMessage')}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="feedback-form">

            <div className="form-group">
              <label htmlFor="type">{t('feedbackType')}</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="feedback">{t('feedbackOption')}</option>
                <option value="feature">{t('featureRequestOption')}</option>
                <option value="bug">{t('bugReportOption')}</option>
                <option value="other">{t('otherOption')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subject">{t('subject')}</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder={t('subjectPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">{t('message')} *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t('messagePlaceholder')}
                rows={6}
                required
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner" />
                  {t('submitting')}
                </>
              ) : (
                <>
                  <IoSendOutline size={20} />
                  {t('submitFeedback')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default FeedbackPage; 